import { useEffect, useRef, useState } from 'react';
import marketStream from '../services/marketStream';

/**
 * Subscribe to live prices for a set of symbols.
 *
 * Returns a `{ SYMBOL: { ltp, ts } }` map that updates as ticks arrive, plus the
 * connection status so a caller can badge "live" vs "delayed".
 *
 * Intentionally additive: callers keep their existing REST polling and treat
 * this as an overlay. If the socket never connects, nothing breaks.
 */
export default function useMarketStream(token, symbols, { enabled = true } = {}) {
  const [ticks, setTicks] = useState({});
  const [status, setStatus] = useState(marketStream.getStatus());

  // Join the symbol list so the effect re-runs on content change rather than on
  // every render (a fresh array literal would otherwise resubscribe constantly).
  const key = (symbols || []).join(',');
  const tickRef = useRef({});

  useEffect(() => {
    if (!token) return undefined;
    marketStream.setToken(token);
  }, [token]);

  useEffect(() => marketStream.onStatus(setStatus), []);

  useEffect(() => {
    if (!enabled || !token || !key) return undefined;

    const unsubscribe = marketStream.subscribe(key.split(','), (batch) => {
      // Merge into a ref first, then publish once — avoids a stale-closure
      // read of `ticks` and keeps updates to one setState per batch.
      tickRef.current = { ...tickRef.current, ...batch };
      setTicks(tickRef.current);
    });

    return unsubscribe;
  }, [token, key, enabled]);

  return { ticks, status, isLive: status.status === 'open' && !status.degraded };
}
